/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { VariableKey, type VariableMap } from '../types';
import { formatDateUTC, DATE, getSurveyDateRange, TimeFrameType } from '@utils/date';
import { getPeriodName, DataPeriods } from '@utils/dataPeriods';
import type { DashboardFilterData } from '@g17eco/types/dashboard';

interface BuildVariableMapParams {
  currentSubsidiaryName?: string;
  rootSubsidiaryName?: string;
  dataFilters: DashboardFilterData;
  /**
   * Fallback value to display when a variable is missing or undefined
   * @default '-'
   */
  fallback?: string;
}

export const buildVariableMap = ({
  currentSubsidiaryName,
  rootSubsidiaryName,
  dataFilters,
  fallback = '-',
}: BuildVariableMapParams): VariableMap => {
  const { timeRange, dateRange, period } = dataFilters;

  let duration = fallback;

  if (timeRange === TimeFrameType.Custom) {
    // Use custom dateRange for custom time range
    const startDate = dateRange?.startDate;
    const endDate = dateRange?.endDate;
    duration = startDate && endDate ? getDurationLabel(startDate, endDate, fallback) : fallback;
  } else if (timeRange === TimeFrameType.AllTime) {
    duration = 'All time';
  } else if (timeRange !== undefined) {
    // Resolve timeRange to start/end dates for predefined ranges (e.g., 12, 6, 3, 1, '12-months', '6-months', etc.)
    const resolvedRange = getSurveyDateRange(timeRange);
    const startDate = resolvedRange.startDate;
    const endDate = resolvedRange.endDate;
    duration = startDate && endDate ? getDurationLabel(startDate, endDate, fallback) : fallback;
  }

  // Convert period to friendly display name
  const periodName = getPeriodName(period as DataPeriods) || fallback;

  const variableMap: VariableMap = {
    [VariableKey.CURRENT_SUBSIDIARY]: currentSubsidiaryName || fallback,
    [VariableKey.ROOT_SUBSIDIARY]: rootSubsidiaryName || fallback,
    [VariableKey.PERIOD]: periodName,
    [VariableKey.DURATION]: duration,
  };

  return variableMap;
};

const getDurationLabel = (startDate: Date, endDate: Date, fallback = ''): string => {
  const startFormatted = formatDateUTC(startDate, DATE.MONTH_YEAR_SHORT);
  const endFormatted = formatDateUTC(endDate, DATE.MONTH_YEAR_SHORT);

  if (!startFormatted || !endFormatted) {
    return fallback;
  }

  return `${startFormatted} - ${endFormatted}`;
};
